// packages
import cx from 'classnames';
import { Component, h } from 'preact';

// components
import CandidateRow from './components/CandidateRow';
import Contest from './components/Contest';
import NewContestShowcase from './components/NewContestShowcase';
import YourRaces from './components/YourRaces';
import Portal from './components/Portal';
import SeatPartyDivider from './components/SeatPartyDivider';
import Ad from './components/Ad';

import find from './utils/find';
import {
  determineContestTitle,
  chunk,
  lastUpdatedFormat,
} from './election-utils';
import {
  RESULTS_IN_USE,
  visibleCandidatesCount,
  showLastUpdated,
} from './endpoints';
import getJSON from './utils/get-json';
import { LENGTH_OF_CHECK_INTERVAL } from './constants';

type State = {
  data: { [key: string]: any };
  now: Date | null;
};

class App extends Component<{}, State> {
  timer!: number;

  constructor() {
    super();

    this.state = {
      data: [],
      now: null,
      timer: 0,
    };

    this.fetchData = this.fetchData.bind(this);
  }

  fetchData() {
    const now = new Date();
    console.info('Requesting:', now);

    const asOf = lastUpdatedFormat(now);

    if (showLastUpdated) {
      document.querySelector('.counter').innerHTML = asOf;
      document.querySelector('.counter-mobile .counter').innerHTML = asOf;
    }

    getJSON(RESULTS_IN_USE).then(data =>
      this.setState({ data, asOf }, this.startTimer)
    );
  }

  startTimer() {
    this.timer = window.setTimeout(this.fetchData, LENGTH_OF_CHECK_INTERVAL);
  }

  clearTimer() {
    window.clearTimeout(this.timer);
  }

  componentDidMount() {
    this.fetchData();
  }

  componentWillUnmount() {
    this.clearTimer();
  }

  componentDidUpdate() {}

  render() {
    const { notes, annotations } = this.props;
    const { data, asOf } = this.state;

    const seats_mapped = data.map(number => ({
      office: number.office,
      office_slug: number.office_slug,
    }));

    function unique(array, propertyName) {
      return array.filter(
        (e, i) =>
          array.findIndex(a => a[propertyName] === e[propertyName]) === i
      );
    }

    let unique_seats = unique(seats_mapped, 'office_slug');

    // In order to sprinkle ads between races in sections where there a lot of races like the Texas House, we need to divide them into groups of nth races to make it into different divs

    const texasHouse = unique_seats.filter(
      contest => contest.office === 'Texas House'
    );

    const usHouse = unique_seats.filter(
      contest => contest.office === 'U.S. House'
    );

    let texasHouseGroups, usHouseGroups;

    if (texasHouse.length) {
      texasHouseGroups = chunk(texasHouse, 28);
      usHouseGroups = chunk(usHouse, 24);
    }

    return (
      <div class="results">
        <Portal
          node={document.getElementById('lookup-select')}
          ref={ref => (this._yourPortal = ref)}
        >
          <YourRaces
            contests={data}
            notes={notes}
            annotations={annotations}
            asOf={asOf}
          />
        </Portal>

        {/* SENATE DEMS */}
        <NewContestShowcase
          contest={find(
            data,
            contest =>
              contest.office === 'U.S. Senate' && contest.party === 'Democrat'
          )}
          container={document.querySelector('#us-senate .democrat')}
          notes={notes.filter(item => item.type === 'us-senate-Democrat')}
          annotations={annotations.filter(
            item => item.type === 'us-senate-Democrat'
          )}
          asOf={asOf}
        />

        {/* SENATE GOP */}
        <NewContestShowcase
          contest={find(
            data,
            contest =>
              contest.office === 'U.S. Senate' && contest.party === 'Republican'
          )}
          container={document.querySelector('#us-senate .republican')}
          notes={notes.filter(item => item.type === 'us-senate-Republican')}
          annotations={annotations.filter(item => item.type === 'us-senate-Republican')}
          asOf={asOf}
        />

        <Portal
          node={document.querySelector(
            '#governor .contests'
          )}
          ref={ref => (this._supremePortal = ref)}
        >
          <div class="office__contests">
            {unique_seats
              .filter(contest => contest.office === 'Governor')
              .map(contest => (
                <Contest
                  key={contest.id}
                  contest={data.filter(
                    item => item.office_slug === contest.office_slug
                  )}
                  notes={notes.filter(
                    item => item.type === contest.office_slug
                  )}
                  tableHeader={CandidateRow.Header}
                  columnCount={CandidateRow.columnCount}
                  contestTitle={determineContestTitle(
                    data.filter(
                      item => item.office_slug === contest.office_slug
                    )[0]
                  )}
                  // includeCountyRundown
                  renderParty={party => (
                    <SeatPartyDivider
                      partyName={party.party}
                      partyBallot={party}
                      tableHeader={CandidateRow.Header}
                      annotations={annotations.filter(
                        item =>
                          item.type === party.office_slug + '-' + party.party
                      )}
                      notes={notes.filter(
                        item =>
                          item.type === contest.office_slug + '-' + party.party
                      )}
                      source={party.source}
                      asOf={asOf}
                      columnCount={CandidateRow.columnCount}
                      renderCandidate={(
                        candidate,
                        idx,
                        viewAllCandidates,
                        { totalVotes }
                      ) => (
                        <CandidateRow
                          key={candidate.id}
                          ballotName={candidate.name}
                          candidateId={candidate.id}
                          contestIsCalled={party.is_called}
                          contestResult={party.contest_result}
                          candidatesNum={party.candidacies.length}
                          isIncumbent={candidate.is_incumbent}
                          isWinner={candidate.is_winner}
                          isViewable={
                            viewAllCandidates || idx < visibleCandidatesCount
                          }
                          party={candidate.party}
                          totalVotesInContest={totalVotes}
                          votes={candidate.votes}
                        />
                      )}
                    />
                  )}
                />
              ))}
          </div>
        </Portal>

        {/* AG GOP */}
        <NewContestShowcase
          contest={find(
            data,
            contest =>
              contest.office === 'Attorney General' && contest.party === 'Republican')}
          container={document.querySelector('#attorney-general .republican')}
          asOf={asOf}
        />

        {/* AG DEM */}
        <NewContestShowcase
          contest={find(
            data,
            contest =>
              contest.office === 'Attorney General' && contest.party === 'Democrat')}
          container={document.querySelector('#attorney-general .democrat')}
          asOf={asOf}
        />

        {/* comptroller GOP MAP */}
        <NewContestShowcase
          contest={find(
            data,
            contest =>
              contest.office === 'Comptroller' && contest.party === 'Republican')}
          container={document.querySelector('#comptroller .republican')}
          asOf={asOf}
        />

        {/* comptroller DEM */}
        <NewContestShowcase
          contest={find(
            data,
            contest =>
              contest.office === 'Comptroller' && contest.party === 'Democrat')}
          container={document.querySelector('#comptroller .democrat')}
          asOf={asOf}
        />

        <Portal
          node={document.querySelector(
            '#lieutenant-governor .contests'
          )}
          ref={ref => (this._supremePortal = ref)}
        >
          <div class="office__contests">
            {unique_seats
              .filter(contest => contest.office === 'Lieutenant Governor')
              .map(contest => (
                <Contest
                  key={contest.id}
                  contest={data.filter(
                    item => item.office_slug === contest.office_slug
                  )}
                  notes={notes.filter(
                    item => item.type === contest.office_slug
                  )}
                  tableHeader={CandidateRow.Header}
                  columnCount={CandidateRow.columnCount}
                  contestTitle={determineContestTitle(
                    data.filter(
                      item => item.office_slug === contest.office_slug
                    )[0]
                  )}
                  // includeCountyRundown
                  renderParty={party => (
                    <SeatPartyDivider
                      partyName={party.party}
                      partyBallot={party}
                      tableHeader={CandidateRow.Header}
                      annotations={annotations.filter(
                        item =>
                          item.type === party.office_slug + '-' + party.party
                      )}
                      notes={notes.filter(
                        item =>
                          item.type === contest.office_slug + '-' + party.party
                      )}
                      source={party.source}
                      asOf={asOf}
                      columnCount={CandidateRow.columnCount}
                      renderCandidate={(
                        candidate,
                        idx,
                        viewAllCandidates,
                        { totalVotes }
                      ) => (
                        <CandidateRow
                          key={candidate.id}
                          ballotName={candidate.name}
                          candidateId={candidate.id}
                          contestIsCalled={party.is_called}
                          contestResult={party.contest_result}
                          candidatesNum={party.candidacies.length}
                          isIncumbent={candidate.is_incumbent}
                          isWinner={candidate.is_winner}
                          isViewable={
                            viewAllCandidates || idx < visibleCandidatesCount
                          }
                          party={candidate.party}
                          totalVotesInContest={totalVotes}
                          votes={candidate.votes}
                        />
                      )}
                    />
                  )}
                />
              ))}
          </div>
        </Portal>

        <Portal
          node={document.querySelector(
            '#agriculture-commissioner .contests'
          )}
          ref={ref => (this._supremePortal = ref)}
        >
          <div class="office__contests">
            {unique_seats
              .filter(contest => contest.office === 'Agriculture Commissioner')
              .map(contest => (
                <Contest
                  key={contest.id}
                  contest={data.filter(
                    item => item.office_slug === contest.office_slug
                  )}
                  notes={notes.filter(
                    item => item.type === contest.office_slug
                  )}
                  tableHeader={CandidateRow.Header}
                  columnCount={CandidateRow.columnCount}
                  contestTitle={determineContestTitle(
                    data.filter(
                      item => item.office_slug === contest.office_slug
                    )[0]
                  )}
                  // includeCountyRundown
                  renderParty={party => (
                    <SeatPartyDivider
                      partyName={party.party}
                      partyBallot={party}
                      tableHeader={CandidateRow.Header}
                      annotations={annotations.filter(
                        item =>
                          item.type === party.office_slug + '-' + party.party
                      )}
                      notes={notes.filter(
                        item =>
                          item.type === contest.office_slug + '-' + party.party
                      )}
                      source={party.source}
                      asOf={asOf}
                      columnCount={CandidateRow.columnCount}
                      renderCandidate={(
                        candidate,
                        idx,
                        viewAllCandidates,
                        { totalVotes }
                      ) => (
                        <CandidateRow
                          key={candidate.id}
                          ballotName={candidate.name}
                          candidateId={candidate.id}
                          contestIsCalled={party.is_called}
                          contestResult={party.contest_result}
                          candidatesNum={party.candidacies.length}
                          isIncumbent={candidate.is_incumbent}
                          isWinner={candidate.is_winner}
                          isViewable={
                            viewAllCandidates || idx < visibleCandidatesCount
                          }
                          party={candidate.party}
                          totalVotesInContest={totalVotes}
                          votes={candidate.votes}
                        />
                      )}
                    />
                  )}
                />
              ))}
          </div>
        </Portal>

        <Portal
          node={document.querySelector(
            '#land-commissioner .contests'
          )}
          ref={ref => (this._supremePortal = ref)}
        >
          <div class="office__contests">
            {unique_seats
              .filter(contest => contest.office === 'Land Commissioner')
              .map(contest => (
                <Contest
                  key={contest.id}
                  contest={data.filter(
                    item => item.office_slug === contest.office_slug
                  )}
                  notes={notes.filter(
                    item => item.type === contest.office_slug
                  )}
                  tableHeader={CandidateRow.Header}
                  columnCount={CandidateRow.columnCount}
                  contestTitle={determineContestTitle(
                    data.filter(
                      item => item.office_slug === contest.office_slug
                    )[0]
                  )}
                  // includeCountyRundown
                  renderParty={party => (
                    <SeatPartyDivider
                      partyName={party.party}
                      partyBallot={party}
                      tableHeader={CandidateRow.Header}
                      annotations={annotations.filter(
                        item =>
                          item.type === party.office_slug + '-' + party.party
                      )}
                      notes={notes.filter(
                        item =>
                          item.type === contest.office_slug + '-' + party.party
                      )}
                      source={party.source}
                      asOf={asOf}
                      columnCount={CandidateRow.columnCount}
                      renderCandidate={(
                        candidate,
                        idx,
                        viewAllCandidates,
                        { totalVotes }
                      ) => (
                        <CandidateRow
                          key={candidate.id}
                          ballotName={candidate.name}
                          candidateId={candidate.id}
                          contestIsCalled={party.is_called}
                          contestResult={party.contest_result}
                          candidatesNum={party.candidacies.length}
                          isIncumbent={candidate.is_incumbent}
                          isWinner={candidate.is_winner}
                          isViewable={
                            viewAllCandidates || idx < visibleCandidatesCount
                          }
                          party={candidate.party}
                          totalVotesInContest={totalVotes}
                          votes={candidate.votes}
                        />
                      )}
                    />
                  )}
                />
              ))}
          </div>
        </Portal>

        <Portal
          node={document.querySelector('#us-house .contests')}
          ref={ref => (this._usPortal = ref)}
        >
          <div class="office__contests">
            {usHouse.length > 0 &&
              usHouseGroups.map(contestGroup => (
                <div class="contest-group">
                  <div class="seat__contests-double">
                    {contestGroup.map(contest => (
                      <Contest
                        key={contest.id}
                        contest={data.filter(
                          item => item.office_slug === contest.office_slug
                        )}
                        tableHeader={CandidateRow.Header}
                        columnCount={CandidateRow.columnCount}
                        contestTitle={determineContestTitle(
                          data.filter(
                            item => item.office_slug === contest.office_slug
                          )[0]
                        )}
                        renderParty={party => (
                          <SeatPartyDivider
                            partyName={party.party}
                            partyBallot={party}
                            tableHeader={CandidateRow.Header}
                            annotations={annotations.filter(
                              item =>
                                item.type ===
                                party.office_slug + '-' + party.party
                            )}
                            notes={notes.filter(
                              item =>
                                item.type ===
                                contest.office_slug + '-' + party.party
                            )}
                            source={party.source}
                            columnCount={CandidateRow.columnCount}
                            asOf={asOf}
                            renderCandidate={(
                              candidate,
                              idx,
                              viewAllCandidates,
                              { totalVotes }
                            ) => (
                              <CandidateRow
                                key={candidate.id}
                                ballotName={candidate.name}
                                candidateId={candidate.id}
                                contestIsCalled={party.is_called}
                                contestResult={party.contest_result}
                                candidatesNum={party.candidacies.length}
                                isIncumbent={candidate.is_incumbent}
                                isWinner={candidate.is_winner}
                                isViewable={
                                  viewAllCandidates ||
                                  idx < visibleCandidatesCount
                                }
                                party={candidate.party}
                                totalVotesInContest={totalVotes}
                                votes={candidate.votes}
                              />
                            )}
                          />
                        )}
                      />
                    ))}
                  </div>
                  <Ad />
                </div>
              ))}
          </div>
        </Portal>
        <Portal
          node={document.querySelector(
            '#railroad-commission-of-texas .contests'
          )}
          ref={ref => (this._supremePortal = ref)}
        >
          <div class="office__contests">
            {unique_seats
              .filter(contest => contest.office === 'Texas Railroad Commission')
              .map(contest => (
                <Contest
                  key={contest.id}
                  contest={data.filter(
                    item => item.office_slug === contest.office_slug
                  )}
                  notes={notes.filter(
                    item => item.type === contest.office_slug
                  )}
                  tableHeader={CandidateRow.Header}
                  columnCount={CandidateRow.columnCount}
                  contestTitle={determineContestTitle(
                    data.filter(
                      item => item.office_slug === contest.office_slug
                    )[0]
                  )}
                  // includeCountyRundown
                  renderParty={party => (
                    <SeatPartyDivider
                      partyName={party.party}
                      partyBallot={party}
                      tableHeader={CandidateRow.Header}
                      annotations={annotations.filter(
                        item =>
                          item.type === party.office_slug + '-' + party.party
                      )}
                      notes={notes.filter(
                        item =>
                          item.type === contest.office_slug + '-' + party.party
                      )}
                      source={party.source}
                      asOf={asOf}
                      columnCount={CandidateRow.columnCount}
                      renderCandidate={(
                        candidate,
                        idx,
                        viewAllCandidates,
                        { totalVotes }
                      ) => (
                        <CandidateRow
                          key={candidate.id}
                          ballotName={candidate.name}
                          candidateId={candidate.id}
                          contestIsCalled={party.is_called}
                          contestResult={party.contest_result}
                          candidatesNum={party.candidacies.length}
                          isIncumbent={candidate.is_incumbent}
                          isWinner={candidate.is_winner}
                          isViewable={
                            viewAllCandidates || idx < visibleCandidatesCount
                          }
                          party={candidate.party}
                          totalVotesInContest={totalVotes}
                          votes={candidate.votes}
                        />
                      )}
                    />
                  )}
                />
              ))}
          </div>
        </Portal>
        <Portal
          node={document.querySelector('#texas-supreme-court .contests')}
          ref={ref => (this._supremePortal = ref)}
        >
          <div class="office__contests">
            {unique_seats
              .filter(contest => contest.office === 'Texas Supreme Court')
              .map(contest => (
                <Contest
                  key={contest.id}
                  contest={data.filter(
                    item => item.office_slug === contest.office_slug
                  )}
                  notes={notes.filter(
                    item => item.type === contest.office_slug
                  )}
                  tableHeader={CandidateRow.Header}
                  columnCount={CandidateRow.columnCount}
                  contestTitle={determineContestTitle(
                    data.filter(
                      item => item.office_slug === contest.office_slug
                    )[0]
                  )}
                  renderParty={party => (
                    <SeatPartyDivider
                      partyName={party.party}
                      partyBallot={party}
                      tableHeader={CandidateRow.Header}
                      annotations={annotations.filter(
                        item =>
                          item.type === party.office_slug + '-' + party.party
                      )}
                      notes={notes.filter(
                        item =>
                          item.type === contest.office_slug + '-' + party.party
                      )}
                      source={party.source}
                      asOf={asOf}
                      columnCount={CandidateRow.columnCount}
                      renderCandidate={(
                        candidate,
                        idx,
                        viewAllCandidates,
                        { totalVotes }
                      ) => (
                        <CandidateRow
                          key={candidate.id}
                          ballotName={candidate.name}
                          candidateId={candidate.id}
                          contestIsCalled={party.is_called}
                          contestResult={party.contest_result}
                          candidatesNum={party.candidacies.length}
                          isIncumbent={candidate.is_incumbent}
                          isWinner={candidate.is_winner}
                          isViewable={
                            viewAllCandidates || idx < visibleCandidatesCount
                          }
                          party={candidate.party}
                          totalVotesInContest={totalVotes}
                          votes={candidate.votes}
                        />
                      )}
                    />
                  )}
                />
              ))}
          </div>
        </Portal>
        <Portal
          node={document.querySelector('#state-board-of-education .contests')}
          ref={ref => (this._sboePortal = ref)}
        >
          <div class="office__contests">
            {unique_seats
              .filter(contest => contest.office === 'State Board of Education')
              .map(contest => (
                <Contest
                  key={contest.id}
                  contest={data.filter(
                    item => item.office_slug === contest.office_slug
                  )}
                  notes={notes.filter(
                    item => item.type === contest.office_slug
                  )}
                  tableHeader={CandidateRow.Header}
                  columnCount={CandidateRow.columnCount}
                  contestTitle={determineContestTitle(
                    data.filter(
                      item => item.office_slug === contest.office_slug
                    )[0]
                  )}
                  renderParty={party => (
                    <SeatPartyDivider
                      partyName={party.party}
                      partyBallot={party}
                      tableHeader={CandidateRow.Header}
                      annotations={annotations.filter(
                        item =>
                          item.type === party.office_slug + '-' + party.party
                      )}
                      notes={notes.filter(
                        item =>
                          item.type === contest.office_slug + '-' + party.party
                      )}
                      source={party.source}
                      asOf={asOf}
                      columnCount={CandidateRow.columnCount}
                      renderCandidate={(
                        candidate,
                        idx,
                        viewAllCandidates,
                        { totalVotes }
                      ) => (
                        <CandidateRow
                          key={candidate.id}
                          ballotName={candidate.name}
                          candidateId={candidate.id}
                          contestIsCalled={party.is_called}
                          contestResult={party.contest_result}
                          candidatesNum={party.candidacies.length}
                          isIncumbent={candidate.is_incumbent}
                          isWinner={candidate.is_winner}
                          isViewable={
                            viewAllCandidates || idx < visibleCandidatesCount
                          }
                          party={candidate.party}
                          totalVotesInContest={totalVotes}
                          votes={candidate.votes}
                        />
                      )}
                    />
                  )}
                />
              ))}
          </div>
        </Portal>
        <Portal
          node={document.querySelector(
            '#texas-court-of-criminal-appeals .contests'
          )}
          ref={ref => (this._crimPortal = ref)}
        >
          <div class="office__contests">
            {unique_seats
              .filter(
                contest => contest.office === 'Texas Court of Criminal Appeals'
              )
              .map(contest => (
                <Contest
                  key={contest.id}
                  contest={data.filter(
                    item => item.office_slug === contest.office_slug
                  )}
                  notes={notes.filter(
                    item => item.type === contest.office_slug
                  )}
                  tableHeader={CandidateRow.Header}
                  columnCount={CandidateRow.columnCount}
                  contestTitle={determineContestTitle(
                    data.filter(
                      item => item.office_slug === contest.office_slug
                    )[0]
                  )}
                  renderParty={party => (
                    <SeatPartyDivider
                      partyName={party.party}
                      partyBallot={party}
                      tableHeader={CandidateRow.Header}
                      annotations={annotations.filter(
                        item =>
                          item.type === party.office_slug + '-' + party.party
                      )}
                      notes={notes.filter(
                        item =>
                          item.type === contest.office_slug + '-' + party.party
                      )}
                      source={party.source}
                      asOf={asOf}
                      columnCount={CandidateRow.columnCount}
                      renderCandidate={(
                        candidate,
                        idx,
                        viewAllCandidates,
                        { totalVotes }
                      ) => (
                        <CandidateRow
                          key={candidate.id}
                          ballotName={candidate.name}
                          candidateId={candidate.id}
                          contestIsCalled={party.is_called}
                          contestResult={party.contest_result}
                          candidatesNum={party.candidacies.length}
                          isIncumbent={candidate.is_incumbent}
                          isWinner={candidate.is_winner}
                          isViewable={
                            viewAllCandidates || idx < visibleCandidatesCount
                          }
                          party={candidate.party}
                          totalVotesInContest={totalVotes}
                          votes={candidate.votes}
                        />
                      )}
                    />
                  )}
                />
              ))}
          </div>
        </Portal>
        <Portal
          node={document.querySelector('#texas-senate .contests')}
          ref={ref => (this._senatePortal = ref)}
        >
          <div class="office__contests">
            {unique_seats
              .filter(contest => contest.office === 'Texas Senate')
              .map(contest => (
                <Contest
                  key={contest.id}
                  contest={data.filter(
                    item => item.office_slug === contest.office_slug
                  )}
                  notes={notes.filter(
                    item => item.type === contest.office_slug
                  )}
                  tableHeader={CandidateRow.Header}
                  columnCount={CandidateRow.columnCount}
                  contestTitle={determineContestTitle(
                    data.filter(
                      item => item.office_slug === contest.office_slug
                    )[0]
                  )}
                  renderParty={party => (
                    <SeatPartyDivider
                      partyName={party.party}
                      partyBallot={party}
                      tableHeader={CandidateRow.Header}
                      annotations={annotations.filter(
                        item =>
                          item.type === party.office_slug + '-' + party.party
                      )}
                      notes={notes.filter(
                        item =>
                          item.type === contest.office_slug + '-' + party.party
                      )}
                      source={party.source}
                      asOf={asOf}
                      columnCount={CandidateRow.columnCount}
                      renderCandidate={(
                        candidate,
                        idx,
                        viewAllCandidates,
                        { totalVotes }
                      ) => (
                        <CandidateRow
                          key={candidate.id}
                          ballotName={candidate.name}
                          candidateId={candidate.id}
                          contestIsCalled={party.is_called}
                          contestResult={party.contest_result}
                          candidatesNum={party.candidacies.length}
                          isIncumbent={candidate.is_incumbent}
                          isWinner={candidate.is_winner}
                          isViewable={
                            viewAllCandidates || idx < visibleCandidatesCount
                          }
                          party={candidate.party}
                          totalVotesInContest={totalVotes}
                          votes={candidate.votes}
                        />
                      )}
                    />
                  )}
                />
              ))}
          </div>
        </Portal>
        <Portal
          node={document.querySelector('#texas-house .contests')}
          ref={ref => (this._housePortal = ref)}
        >
          <div class="office__contests">
            {texasHouse.length > 0 &&
              texasHouseGroups.map(contestGroup => (
                <div class="contest-group">
                  <div class="seat__contests-double">
                    {contestGroup.map(contest => (
                      <Contest
                        key={contest.id}
                        contest={data.filter(
                          item => item.office_slug === contest.office_slug
                        )}
                        notes={notes.filter(
                          item => item.type === contest.office_slug
                        )}
                        tableHeader={CandidateRow.Header}
                        columnCount={CandidateRow.columnCount}
                        contestTitle={determineContestTitle(
                          data.filter(
                            item => item.office_slug === contest.office_slug
                          )[0]
                        )}
                        renderParty={party => (
                          <SeatPartyDivider
                            partyName={party.party}
                            partyBallot={party}
                            tableHeader={CandidateRow.Header}
                            annotations={annotations.filter(
                              item =>
                                item.type ===
                                party.office_slug + '-' + party.party
                            )}
                            notes={notes.filter(
                              item =>
                                item.type ===
                                contest.office_slug + '-' + party.party
                            )}
                            source={party.source}
                            asOf={asOf}
                            columnCount={CandidateRow.columnCount}
                            renderCandidate={(
                              candidate,
                              idx,
                              viewAllCandidates,
                              { totalVotes }
                            ) => (
                              <CandidateRow
                                key={candidate.id}
                                ballotName={candidate.name}
                                candidateId={candidate.id}
                                contestIsCalled={party.is_called}
                                contestResult={party.contest_result}
                                candidatesNum={party.candidacies.length}
                                isIncumbent={candidate.is_incumbent}
                                isWinner={candidate.is_winner}
                                isViewable={
                                  viewAllCandidates ||
                                  idx < visibleCandidatesCount
                                }
                                party={candidate.party}
                                totalVotesInContest={totalVotes}
                                votes={candidate.votes}
                              />
                            )}
                          />
                        )}
                      />
                    ))}
                  </div>
                  <Ad />
                </div>
              ))}
          </div>
        </Portal>
      </div>
    );
  }
}

export default App;
